import { getDefaultSmn } from "../../nitrogen/smn"
import { INitrogenAnalysis } from "../../nitrogen/types"
import { FieldViewModel, IOmniaShell, IParam, numericTextSort, OmniaObjectType, param } from "../../omnia"
import { INitrogenAnalysisBindingModel, ISoilService, NitrogenAnalysisType } from "../../types"
import { getActivity } from "../activity"
import { AnalysisEditCtrl } from "../edit"

/// <reference path="../edit.ts" />

export class NitrogenAllocVM {
    public cropping: FieldViewModel;
    public allocCropN: number;
    public allocSMN: number;

    constructor(cropping: FieldViewModel, allocCropN?: number, allocSMN?: number) {
        this.cropping = cropping;
        this.allocCropN = allocCropN;
        this.allocSMN = allocSMN;
    }
}

export class NitrogenAnalysisEditCtrl extends AnalysisEditCtrl<INitrogenAnalysis> {

    static createPopup(): ng.IDirective {
        return super.create(NitrogenAnalysisEditCtrl);
    };

    private _nitrogenAnalysis: INitrogenAnalysis;

    private allocCropN: number;
    private allocSMN: number;

    private analysisTypes: IParam[];

    selectedNAllocTargets: NitrogenAllocVM[];
    deletedNAllocTargets: NitrogenAllocVM[];

    static $inject = ["$scope", "OmniaSoilService", "OmniaShell"];


    constructor($scope: ng.IScope, svc: ISoilService, shell: IOmniaShell) {
        super($scope, svc, shell);
        super.setup();

        this.hideDateInId = true;
    }

    protected getTypeInfo() {
        return { name: "Nitrogen analysis", title: "name: nitrogen analysis", type: OmniaObjectType.NitrogenAnalysis }
    }

    protected getPopupTemplate(): string {
        return "app/soil/edit/nitrogen/nitrogen.html";
    }

    protected isCroppingScoped() {
        return true;
    }

    protected setupAnalysis(a: INitrogenAnalysis) {

        this._nitrogenAnalysis = a;

        this.analysisTypes = param.NitrogenAnalysisType().filter(p => p.value !== 3)
        this.allocTargets = this.svc.cropping.filter(c => c.currentYear);
        this.allocTargets = numericTextSort(this.allocTargets, t => t.description);

        this.selectedNAllocTargets = !!a && a.Allocations && a.Allocations.length
            ? a.Allocations.map(l => {
                var cropping = this.allocTargets.find(c => c.croppingId === l.CroppingId);
                return cropping ? new NitrogenAllocVM(cropping, l.CropN, l.SMN) : null;
            }).filter(l => !!l) : this.getAllocFromCmd();

        this.deletedNAllocTargets = [];

    }

    protected getActivity() {
        const a = this._nitrogenAnalysis
        return getActivity(this.newAnalysis, { type: OmniaObjectType.NitrogenAnalysis, id: a?.NitrogenAnalysisId, number: a?.AnalysisNumber })
    }

    getAvailableAllocTargets() {

        const { allocTargets, selectedNAllocTargets, deletedNAllocTargets } = this

        let t = allocTargets

        if (selectedNAllocTargets) {
            t = t.filter(t => selectedNAllocTargets.every(s => s.cropping.croppingId !== t.croppingId))
        }

        if (deletedNAllocTargets) {
            t = t.filter(t => deletedNAllocTargets.every(s => s.cropping.croppingId !== t.croppingId))
        }

        return t

    }

    getAllocFromCmd(): NitrogenAllocVM[] {
        if (this.cmd && this.cmd.alloc) {
            if (this.cmd.alloc.cropping) {
                return [
                    new NitrogenAllocVM(this.allocTargets.find(a => a.croppingId === this.cmd.alloc.croppingId))
                ];
            }

            var fieldCroppings = this.cmd.alloc.field.Cropping.filter(c => c.YearId === this.svc.yearId);

            if (fieldCroppings.length === 1) {
                return [
                    new NitrogenAllocVM(this.allocTargets.find(a => a.croppingId === fieldCroppings[0].CroppingId))
                ];
            }
        }

        return [];
    }

    selectCropping(cropping: FieldViewModel) {
        if (cropping) {
            this.currentAllocSelection = null;

            if (this.selectedNAllocTargets.filter(a => a.cropping === cropping).length === 0 &&
                this.deletedNAllocTargets.filter(a => a.cropping === cropping).length === 0) {

                this.selectedNAllocTargets.push(new NitrogenAllocVM(cropping));
            }
        }
    }

    unselectCropping(cropping: NitrogenAllocVM) {
        if (cropping && this.selectedNAllocTargets.indexOf(cropping) !== -1) {

            if (this._nitrogenAnalysis.Allocations && this._nitrogenAnalysis.Allocations.filter(a => a.CroppingId === cropping.cropping.croppingId).length > 0) {
                if (this.deletedNAllocTargets.indexOf(cropping) === -1) {
                    this.deletedNAllocTargets.push(cropping);
                }
            }

            this.selectedNAllocTargets = this.selectedNAllocTargets.filter(f => cropping !== f);
        }
    }

    recoverCropping(cropping: NitrogenAllocVM) {
        if (cropping && this.deletedNAllocTargets.indexOf(cropping) !== -1) {
            this.deletedNAllocTargets = this.deletedNAllocTargets.filter(f => cropping !== f);

            if (this.selectedNAllocTargets.indexOf(cropping) === -1) {
                this.selectedNAllocTargets.push(cropping);
            }
        }
    }

    isSoilMineralNitrogenAnalysis(): boolean {
        return this._nitrogenAnalysis && (this._nitrogenAnalysis.AnalysisType == NitrogenAnalysisType.SMN);
    }

    getTotalSNS(): number {

        if (this._nitrogenAnalysis) {
            var sns = this._nitrogenAnalysis.Nitrogen;
            sns += (this.allocCropN || 0);
            if (this.isSoilMineralNitrogenAnalysis()) sns += (this.allocSMN || 0);
            return sns;
        }

    }

    getTotalSNSFromAlloc(alloc: NitrogenAllocVM) {

        if (this._nitrogenAnalysis) {
            var sns = this._nitrogenAnalysis.Nitrogen;
            sns += (alloc.allocCropN || 0);
            if (this.isSoilMineralNitrogenAnalysis()) sns += (alloc.allocSMN || 0);
            return sns;
        }

    }

    private updateSnsForSMN() {

        var na = this._nitrogenAnalysis;

        na.Nitrogen = na.NitrogenDepth1 + na.NitrogenDepth2 + na.NitrogenDepth3;

    }

    protected saveAnalysis(): ng.IPromise<number> {

        var na = this._nitrogenAnalysis;

        if (!this.isSoilMineralNitrogenAnalysis()) {

            na.NitrogenDepth1 = null;
            na.NitrogenDepth2 = null;
            na.NitrogenDepth3 = null;

        }

        var model: INitrogenAnalysisBindingModel = {
            NitrogenAnalysis: na
        };

        if (this.allocTarget) {

            model.Allocations = [
                { CroppingId: this.allocTarget.croppingId, FieldId: this.allocTarget.fieldId, CropN: this.allocCropN, SMN: this.isSoilMineralNitrogenAnalysis() ? this.allocSMN : null }
            ];

        }

        if (this.selectedNAllocTargets) {
            model.Allocations = this.selectedNAllocTargets.map(a => {
                return { CroppingId: a.cropping.croppingId, FieldId: a.cropping.fieldId, CropN: a.allocCropN, SMN: this.isSoilMineralNitrogenAnalysis() ? a.allocSMN : null };
            });
        }

        return this.svc.saveNitrogenAnalysis(model);

    }


    protected updateOnAllocTargetChanged(target: FieldViewModel) {

        if (target.isCropping()) {
            this.allocSMN = getDefaultSmn(this.svc, this._allocTarget)
        }
    }

    protected deleteAnalysis() {

        let id = this._nitrogenAnalysis.NitrogenAnalysisId;
        if (!id) return null;

        return this.svc.deleteNitrogenAnalysis(id);
    }

}
